<template>
  <div
    class="ctrl"
    :class="{ 'is-collapse': isCollapse }"
    :style="{ flexDirection: props.flexDirection }"
  >
    <el-dropdown
      v-if="project.currentModuleList.length > 1"
      :placement="props.flexDirection === 'row' ? 'bottom-end' : 'right-end'"
      @command="changeModule"
    >
      <span class="dropdown-link">
        <el-icon><Files /></el-icon>
        <span v-show="!props.isCollapse">
          {{ project.currentModule.title }}
          <el-icon class="el-icon--right">
            <arrow-down />
          </el-icon>
        </span>
      </span>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
            v-for="item in project.currentModuleList"
            :key="item.id"
            :command="item.id"
            >{{ item.title }}</el-dropdown-item
          >
        </el-dropdown-menu>
      </template>
    </el-dropdown>
    <el-dropdown
      v-if="project.localeLanguages.length > 1"
      :placement="props.flexDirection === 'row' ? 'bottom-end' : 'right-end'"
      @command="changeLocalLanguage"
    >
      <span class="dropdown-link">
        <svg-icon
          v-if="props.flexDirection !== 'row'"
          icon-class="language"
          style="margin-right: 6px"
        />
        <span class="language-label" v-show="!props.isCollapse">{{
          currentLocalLanguage.label
        }}</span>
        <svg-icon
          v-if="props.flexDirection === 'row'"
          icon-class="language"
          style="margin-left: 6px"
        />
      </span>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
            v-for="item in project.localeLanguages"
            :key="item.value"
            :command="item.value"
            ><span :class="{ 'active-language': item.value === currentLocalLanguage.value }">{{
              item.label
            }}</span></el-dropdown-item
          >
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <template v-if="!isPlatform">
      <el-popover
        v-if="message?.show"
        :placement="placement"
        :width="310"
        :teleported="false"
        trigger="click"
      >
        <template #reference>
          <div class="message" :class="{ 'has-unread': hasUnread }">
            <el-tooltip
              effect="dark"
              :content="$t('systemPage.layout.messageCenter')"
              :placement="placement"
            >
              <el-space>
                <svg-icon icon-class="message-tip" :class="{ active: hasMessage }" />
                <span v-show="!props.isCollapse">{{ $t('systemPage.layout.messageCenter') }}</span>
              </el-space>
            </el-tooltip>
            <span class="dot"></span>
          </div>
        </template>
        <div class="message-box">
          <div class="msg-header">
            <h3>{{ $t('systemPage.layout.messageCenter') }}</h3>
            <el-link v-if="message.historyPage" type="primary" @click="goMessage">{{
              $t('systemPage.layout.viewAll')
            }}</el-link>
          </div>
          <div class="msg-body">
            <div v-if="messageList.length === 0" class="list-empty">{{
              $t('systemPage.layout.noUnreadMessages')
            }}</div>
            <template v-else>
              <div v-for="item in messageList" :key="item.id" class="item" @click="goMessage">
                <div class="item-title">{{ item[message.contentKey] }}</div>
                <div class="time">{{ item[message.timeKey] }}</div>
                <!-- <svg-icon icon-class="delete" @click.stop="delRow(item.id)" /> -->
              </div>
            </template>
          </div>
        </div>
      </el-popover>

      <el-tooltip
        v-if="
          (hasPage('WorkflowManager') || hasPage('WORKFLOW_MANAGER')) &&
          hasPermissionFn('SYSTEM_MANAGER:WORKFLOW_MANAGER', 'GENERAL')
        "
        effect="dark"
        :content="$t('systemPage.layout.workflow')"
        :placement="placement"
      >
        <div class="message" :class="{ 'has-unread': false }">
          <el-space :class="{ active: haswf }" @click="goWotkflow('TODO')">
            <svg-icon :class="{ active: haswf }" icon-class="wotkflow-icon" />
            <span v-show="!props.isCollapse">{{ $t('systemPage.layout.workflow') }}</span>
          </el-space>
          <span class="dot"></span>
        </div>
      </el-tooltip>

      <el-tooltip effect="dark" :content="$t('systemPage.layout.profile')" :placement="placement">
        <el-space :class="{ active: hasInfo }" @click="goInfo">
          <svg-icon :class="{ active: hasInfo }" icon-class="admin" />
          <span v-show="!props.isCollapse">{{
            currentUser.displayName || currentUser.username
          }}</span>
        </el-space>
      </el-tooltip>

      <el-tooltip
        v-if="hasPermissionFn('SYSTEM_MANAGER', 'GENERAL')"
        effect="dark"
        :content="$t('systemPage.layout.settings')"
        :placement="placement"
      >
        <el-space :class="{ active: hasSys }" @click="goSys">
          <svg-icon icon-class="setting" :class="{ active: hasSys }" />
          <span v-show="!props.isCollapse">{{ $t('systemPage.layout.settings') }}</span>
        </el-space>
      </el-tooltip>

      <el-tooltip effect="dark" :content="$t('systemPage.layout.logout')" :placement="placement">
        <el-space @click="logout">
          <svg-icon icon-class="logout" />
          <span v-show="!props.isCollapse">{{ $t('systemPage.layout.logout') }}</span>
        </el-space>
      </el-tooltip>
    </template>
  </div>
</template>

<script lang="ts" setup>
  import { ArrowDown, Files } from '@element-plus/icons-vue'
  import { delMessageReq, queryDatasetData } from '@haohan/clw-api'
  import { useGlobalStore, useProject, useUser } from '@haohan/clw-store'
  import { COMMON_PAGES, emitter, setLocale } from '@haohan/clw-utils'
  import { ElMessage } from 'element-plus'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { usePermissionHook } from '@haohan/clw-hooks'
  import { useI18n } from 'vue-i18n'

  const { t, locale } = useI18n()

  const props = defineProps({
    isCollapse: {
      type: Boolean,
      default: false
    },
    flexDirection: {
      type: String,
      default: 'row'
    }
  })

  const placement = computed(() => {
    return props.flexDirection === 'row' ? 'bottom' : 'right'
  })

  const user = useUser()
  const project = useProject()
  const router = useRouter()
  const route = useRoute()
  const projectId = ref('')
  const globalStore = useGlobalStore()

  const { hasPermissionFn } = usePermissionHook()

  // 是否开启 keep-alive
  const isOpenKeepAlive = computed(() => project.previewKeepAliveRoute.isOpenKeepAlive)

  //切换模块
  async function changeModule(id: any) {
    //先清除所有缓存
    if (isOpenKeepAlive.value) {
      project.clearPreviewKeepAliveRoute(true)
    }
    await project.updateCurrentModule(id)
  }

  const currentUser = computed(() => {
    return user.current
  })
  const currentProject = computed(() => {
    return project.currentProject
  })
  const currentModule = computed(() => {
    return project.currentModule
  })

  const isPlatform = computed(() => {
    return globalStore.isPlatform
  })

  function hasPage(page: string) {
    return (
      currentProject.value?.settings?.comPages?.trim() !== '' &&
      currentProject.value?.settings?.comPages?.includes(page)
    )
  }
  const hasInfo = computed(() => route.path.indexOf('userinfo') > -1)
  const hasSys = computed(() => route.path.indexOf('sys') > -1)
  const hasMessage = computed(() => route.path.indexOf('user-message') > -1)
  const haswf = computed(() => route.path.indexOf('work-flow-page') > -1)

  function goInfo() {
    const originName = 'userinfo'
    router.push({
      name: originName,
      params: { projectId: projectId.value, pageId: route.params.pageId }
    })
  }
  function goSys() {
    // 当前用户拥有的生成端权限
    const permissionNames = currentUser.value?.permissions
      .filter((f: any) => f.type === 'GENERAL')
      ?.map((m: any) => m.name)
    // 从COMMON_PAGES中根据权限筛选页面
    const hasPermissionPages = COMMON_PAGES.filter((f: any) => permissionNames.includes(f.name))
    // 默认跳转第一个页面
    if (hasPermissionPages?.length) {
      //特殊处理，取消菜单选中高亮功能
      router.push({
        name: hasPermissionPages[0].path,
        params: { projectId: projectId.value, pageId: route.params.pageId }
      })
    } else {
      ElMessage.warning(t('systemPage.layout.noPagePermission'))
    }
  }
  // 消息中心
  const message = computed(() => {
    const ms = project.currentProject.settings?.message
    return ms ? JSON.parse(ms) : undefined
  })
  const showMessageFlag = ref(false)
  const messageList = ref<any[]>([])
  const hasUnread = computed(() => messageList.value.some((item) => item.status === 'UN_READ'))
  async function queryMessageFn() {
    // if (!hasPage('UserMessage')) {
    //   return
    // }
    try {
      if (!message.value?.show) {
        return
      }
      const { dataId } = message.value
      if (!dataId || dataId === '') {
        return false
      }
      const params = {
        page: 1,
        size: 10000
      }
      const { data } = await queryDatasetData(currentProject.value.id, dataId, params)
      messageList.value = data.content
    } catch (error) {
      messageList.value = []
    }
  }
  function goMessage(type?: any) {
    showMessageFlag.value = false
    const { historyPage, historyPageType } = message.value
    const pathName = historyPageType !== 'bigScreen' ? 'preview' : 'bigscreen'
    router.push(`/${pathName}/${projectId.value}/${currentModule.value?.id}/${historyPage}`)
  }

  const type4Tab: any = {
    PROC_START: '', //工作流定时开启
    PROC_STOP: '', //工作流定时结束
    TODO: 'ToDoMessage', //收到待办
    TODO_OUT_OF_DATE: 'ToDoMessage', //待办即将过期
    TODO_EXPIRED: 'ToDoMessage', //待办已过期
    FLOW_MANUAL_END: '', //发起人工作流手动停止
    FLOW_END_ACCEPT: 'MyInitiate', //工作流结束，通过
    FLOW_END_REJECT: 'MyInitiate', //工作流结束，驳回
    CC: 'MyProcessed' //已处理
  }

  function goWotkflow(type?: any) {
    showMessageFlag.value = false
    const originName = 'WorkflowPage'
    router.push({
      name: originName,
      params: { projectId: projectId.value, pageId: route.params.pageId },
      query: { tab: type4Tab[type] || '' }
    })
  }

  const delRow = async (id: any) => {
    try {
      await delMessageReq(id)
      ElMessage.success(t('global.operationSuccess'))
      queryMessageFn()
    } catch (error) {}
  }

  async function logout() {
    await user.logout()
    // 避免登录不同用户看到旧页面
    router.push(`/login`)
  }

  onMounted(() => {
    projectId.value = `${route.params.projectId || project.currentProject.id}`
    queryMessageFn()
    emitter.on('change-message-tab', queryMessageFn)
  })

  // 国际化处理
  // 当前选中语言
  const currentLocalLanguage = computed(() => {
    return project.localeLanguages.find((item: any) => item.value === locale.value)
  })

  const changeLocalLanguage = (value: string) => {
    locale.value = value
    setLocale(value)
    // window.location.reload()
  }
</script>

<style lang="scss" scoped>
  .ctrl {
    display: flex;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    gap: 20px;
    color: var(--custom-menu-text-color);

    :deep(.el-popper) {
      border-radius: 12px;
      padding: 0;
    }
    .el-space {
      white-space: nowrap;
    }

    .active {
      // color: var(--custom-menu-active-color);
    }
  }

  .dropdown-link {
    color: var(--custom-menu-text-color);
    display: flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
    .language-label {
      font-size: 12px;
    }
  }
  .svg-icon {
    width: 18px;
    height: 18px;
    color: var(--custom-menu-text-color);
    cursor: pointer;
    outline: none;
  }

  .message-box {
    .msg-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 54px;
      padding: 0 16px 0 20px;
      h3 {
        font-size: 14px;
      }
    }
    .msg-body {
      max-height: 400px;
      overflow: auto;
      padding: 0 0 10px 20px;
      font-size: 14px;
      .item {
        position: relative;
        margin-bottom: 20px;
        line-height: 1.5;
        padding-right: 40px;
        &:hover {
          .svg-icon {
            display: block;
          }
        }
      }
      .item-title {
        color: #606266;
      }
      .time {
        font-size: 12px;
        color: #a1a2a5;
      }
      .svg-icon {
        position: absolute;
        color: #f00;
        top: 50%;
        right: 10px;
        transform: translateY(-50%);
        display: none;
      }
    }

    .list-empty {
      font-size: 12px;
      color: #a1a2a5;
      height: 94px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .message {
    position: relative;
    .dot {
      position: absolute;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      top: -2px;
      right: -4px;
      background-color: transparent;
    }
    &.has-unread {
      .dot {
        background-color: #f01d1d;
      }
    }
  }

  .active-language {
    color: var(--font-color-clickable);
  }
</style>
